import { GhostButton } from '@ardoq/button';
import {
  APIEntityType,
  ArdoqId,
  PersistedExternalDocument,
  type Provenance,
  ReportColumn,
} from '@ardoq/api-types';
import { isShiftXUrl, Maybe, urlUtils } from '@ardoq/common-helpers';
import {
  BooleanDecorator,
  DateDecorator,
  UserDecorator,
} from '@ardoq/decorators';
import { colors, s4 } from '@ardoq/design-tokens';
import { ErrorBoundary } from '@ardoq/error-boundary';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { Box, FlexBox } from '@ardoq/layout';
import { TagContainerWithTruncation } from '@ardoq/snowflakes';
import { logError } from '@ardoq/logging';
import {
  TextOverflow,
  WithPopover,
  TruncatedTextPopover,
} from '@ardoq/popovers';
import {
  ComponentIconRepresentation,
  ComponentRepresentationWithLink,
  MarkdownInPopover,
} from '@ardoq/renderers';
import { tourAnchorIds } from '@ardoq/product-tours';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import {
  ICellRendererParams,
  IGroupCellRendererParams,
  IRowNode,
} from 'ag-grid-enterprise';
import { isEmpty } from 'lodash';
import { CSSProperties, ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import {
  ExtendedRowNode,
  HelperTextRow,
  SavingStatus,
  SharedInventoryContext,
} from '../types';
import { cellOperations } from '../cell/cellOperations';
import { cellRendererPropsOperations } from './cellRendererPropsOperations';
import { columnOperations } from '../columns/columnOperations';
import {
  ComponentCellRenderer,
  ComponentCellRendererProps,
  ColumnWithCustomCellRenderer,
  ReferenceCellRendererProps,
  SharedCellRenderer,
  SharedCellRendererProps,
} from './types';
import { CustomCellRendererName } from './custom/customCellRendererNames';
import { CellSelect } from '@ardoq/select';
import { Badge, StatusType, Tag, TagButton } from '@ardoq/status-ui';
import { graphics, NotApplicable, NoValue, Redacted } from './atoms';
import { ReferenceRenderer } from './ReferenceRenderer';
import { isValidArdoqId } from '@ardoq/validation';
import { t } from '@ardoq/i18n';
import { ProvenanceValue } from '@ardoq/provenance';

const DateOnlyRenderer: SharedCellRenderer<string> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  return value ? <DateDecorator date={value} format="DATE" /> : <NoValue />;
};

const DateTimeRenderer: SharedCellRenderer<string> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  return value ? <DateDecorator date={value} format="DATETIME" /> : <NoValue />;
};

const CheckBoxRenderer: SharedCellRenderer<boolean> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  const { i18nLocale } = props.context;
  return (
    <BooleanDecorator
      checked={value}
      trueValueLabel={t(
        i18nLocale,
        'inventory:cellRenderers.checkbox.checked',
        'Checked'
      )}
      falseValueLabel={t(
        i18nLocale,
        'inventory:cellRenderers.checkbox.unchecked',
        'Unchecked'
      )}
    />
  );
};

const DateOnlyRangeRenderer: SharedCellRenderer<
  Maybe<{ start?: string; end?: string }>
> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  return value ? (
    <DateDecorator dateFrom={value.start} dateUntil={value.end} format="DATE" />
  ) : (
    <NoValue />
  );
};

const DateTimeRangeRenderer: SharedCellRenderer<
  Maybe<{ start?: string; end?: string }>
> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  return value ? (
    <DateDecorator
      dateFrom={value.start}
      dateUntil={value.end}
      format="DATETIME"
    />
  ) : (
    <NoValue />
  );
};

const UserRenderer: SharedCellRenderer<ArdoqId> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const user = cellRendererPropsOperations.getUser(props);
  const { i18nLocale } = props.context;

  return props.value ? (
    <UserDecorator
      name={user?.name}
      email={user?.email ?? undefined}
      image={user?.linkedComponent?.image ?? undefined}
      placeholder={t(
        i18nLocale,
        'inventoryApp:ag.cellRenderer.user.missingInformation',
        { userId: props.value },
        'Missing information for user {{userId}}'
      )}
    />
  ) : (
    <NoValue />
  );
};

const TextAreaRenderer: SharedCellRenderer<string> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  const { i18nLocale } = props.context;

  return value ? (
    <MarkdownInPopover value={value} i18nLocale={i18nLocale} variant="light" />
  ) : (
    <NoValue />
  );
};

const getNumberRenderer =
  (column: ReportColumn): SharedCellRenderer<number> =>
  props => {
    if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
      return <NotApplicable />;
    if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
      return <Redacted />;
    return cellRendererPropsOperations.getNumber(props, column) ?? <NoValue />;
  };

const useChevronKeyboard = (props: ComponentCellRendererProps<string>) => {
  useEffect(() => {
    if (!props.context.hierarchy.isHierarchyEnabled) return;

    const chevrons = Array.from(
      props.eGridCell.querySelectorAll<HTMLElement>(
        '.ag-group-contracted, .ag-group-expanded'
      )
    );

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        // Pass the keyboard event so onRowGroupOpened receives it as
        // `event.event` and tracking can distinguish user-initiated toggles
        // from programmatic ones (e.g. re-expansion after refresh).
        props.node.setExpanded(!props.node.expanded, e);
      }
    };

    const updateAriaAttributes = () => {
      // Read locale from AG Grid context at invocation; the context object is
      // mutated by the Inventory.tsx useEffect when locale changes, and cells
      // re-render via refreshCells({ force: true }) — so this effect re-runs
      // and rewrites the attribute with the latest translation.
      const i18nLocale = props.context.i18nLocale ?? {};
      for (const chevron of chevrons) {
        chevron.setAttribute(
          'aria-label',
          props.node.expanded
            ? t(i18nLocale, 'inventoryApp:ag.cellRenderer.collapse', 'Collapse')
            : t(i18nLocale, 'inventoryApp:ag.cellRenderer.expand', 'Expand')
        );
        chevron.setAttribute('aria-expanded', String(props.node.expanded));
      }
    };

    for (const chevron of chevrons) {
      chevron.setAttribute('tabindex', '0');
      chevron.setAttribute('role', 'button');
      chevron.addEventListener('keydown', onKeyDown);
    }

    updateAriaAttributes();
    props.node.addEventListener('expandedChanged', updateAriaAttributes);

    return () => {
      for (const chevron of chevrons) {
        chevron.removeEventListener('keydown', onKeyDown);
        chevron.removeAttribute('tabindex');
        chevron.removeAttribute('role');
        chevron.removeAttribute('aria-label');
        chevron.removeAttribute('aria-expanded');
      }
      props.node.removeEventListener('expandedChanged', updateAriaAttributes);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- AG Grid surface; see apps/ardoq-front/AGENTS.md
  }, [props.context.hierarchy.isHierarchyEnabled, props.eGridCell, props.node]);
};

const NameRendererContent = styled(FlexBox)`
  padding-right: ${s4};
`;

const greyedOutIconStyle: CSSProperties = { filter: 'grayscale(100%)' };

const NameRenderer: ComponentCellRenderer<string> = props => {
  useChevronKeyboard(props);
  const scopeData = cellRendererPropsOperations.getScopeData(props);
  const value = cellRendererPropsOperations.getValue(props);
  const { i18nLocale } = props.context;

  const savingStatus =
    (props.node as ExtendedRowNode)._savingStatus ?? undefined;

  if (cellRendererPropsOperations.isComponentNotInScopeData(props)) {
    const modelId = cellRendererPropsOperations.getModelId(props);
    const componentTypeId = cellRendererPropsOperations.getTypeId(props);
    const isComponentTypeValid =
      componentTypeId ===
      cellRendererPropsOperations.getComponentType(props)?.id; // this makes sure that the component type is valid, not pasted invalid text

    return (
      <NameRendererContent width="full" gap="small" align="center">
        <FlexBox gap="small">
          {componentTypeId && modelId && scopeData && isComponentTypeValid ? (
            <ComponentIconRepresentation
              entityType={APIEntityType.COMPONENT_TYPE}
              entityId={componentTypeId}
              enhancedScopeData={scopeData}
              graphics={graphics}
              parentEntityId={modelId}
            />
          ) : null}

          <TextOverflow>{value}</TextOverflow>
        </FlexBox>

        {savingStatus && (
          <Box>
            {savingStatus === SavingStatus.PENDING && (
              <Icon
                iconName={IconName.SPINNER}
                iconSize={IconSize.SMALL}
                color={colors.grey500}
              />
            )}
            {savingStatus === SavingStatus.RECENTLY_SAVED && (
              <Badge
                statusType={StatusType.SUCCESS}
                label={t(
                  i18nLocale,
                  'inventoryApp:ag.cellRenderer.name.saved',
                  'Saved'
                )}
              />
            )}
          </Box>
        )}
      </NameRendererContent>
    );
  }

  const componentId = cellRendererPropsOperations.getId(props);
  const canOpenComponentOverview =
    componentId !== null &&
    cellRendererPropsOperations.canOpenComponentOverview(
      props.context,
      componentId
    );
  const isNonMatchingAncestor =
    cellRendererPropsOperations.isNonMatchingAncestor(props);
  return (
    <NameRendererContent
      gap="small"
      width="full"
      data-tour-id={
        props.node.rowIndex === 0
          ? tourAnchorIds.inventoryFirstComponentName
          : undefined
      }
    >
      {componentId && scopeData ? (
        <ComponentIconRepresentation
          entityType={APIEntityType.COMPONENT}
          entityId={componentId}
          enhancedScopeData={scopeData}
          graphics={graphics}
          style={isNonMatchingAncestor ? greyedOutIconStyle : undefined}
        />
      ) : null}
      {canOpenComponentOverview ? (
        <Link // TODO: Use button that looks like link when that's available
          hideIcon
          typography="text2"
          type={
            isNonMatchingAncestor
              ? 'interactiveUnderlinedSubtle'
              : 'interactiveUnderlined'
          }
          onClick={() => {
            const commands = cellRendererPropsOperations.getCommands(props);
            if (componentId)
              commands.showComponentOverviewDrawer(componentId, scopeData);
          }}
        >
          {value}
        </Link>
      ) : (
        <TextOverflow>{value}</TextOverflow>
      )}
    </NameRendererContent>
  );
};

const ParentRenderer: ComponentCellRenderer<ArdoqId> = props => {
  const value = cellRendererPropsOperations.getValue(props);
  const scopeData = cellRendererPropsOperations.getScopeData(props);
  const { i18nLocale } = props.context;

  if (!value || !scopeData) {
    return <NoValue />;
  }

  if (!isValidArdoqId(value)) {
    return <NoValue />;
  }

  const parentComponentInScopeData = scopeData.componentsById[value];
  if (!parentComponentInScopeData) {
    // It's possible that the parent component is not yet in scope data for a short time
    // when we set the parent to a component that was not part of scope data, we display the renderer before
    // we get response with scope data including the new parent component from backend
    return (
      <Tag>
        {t(i18nLocale, 'inventoryApp:ag.cellRenderer.loading', 'Loading…')}
      </Tag>
    );
  }

  return (
    <ComponentRepresentationWithLink
      entityId={value}
      enhancedScopeData={scopeData}
      graphics={graphics}
      variant="light"
    />
  );
};

const URLRenderer: SharedCellRenderer<string> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  if (!value) return <NoValue />;

  return (
    <Link
      href={urlUtils.maybeRelativeUrl(value, isShiftXUrl(value))}
      target="_blank"
      rel="noopener noreferrer"
    >
      {value}
    </Link>
  );
};

const EmailRenderer: SharedCellRenderer<string> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;
  const value = cellRendererPropsOperations.getValue(props);
  if (!value) return <NoValue />;
  return <UserDecorator email={value} name={value} />;
};

const ComponentTypeRenderer: ComponentCellRenderer<ArdoqId> = props => {
  const scopeData = cellRendererPropsOperations.getScopeData(props);
  const componentType = cellRendererPropsOperations.getComponentType(props);
  const modelId = cellRendererPropsOperations.getModelId(props);
  if (!(props.data && componentType && scopeData && modelId))
    return <NoValue />;

  if (createComponentsOperations.isComponentNotPersisted(props.data._id)) {
    // Show the Select for the new component row to highlight that this can be edited
    return (
      <Box width="full">
        <CellSelect
          openMenuOnClick={false} // this is set to false so that clicking on the cell triggers edit mode
          value={componentType.id}
          onValueChange={() => {}}
          options={[{ label: componentType.name, value: componentType.id }]}
          isClearable={false}
        />
      </Box>
    );
  }
  return (
    <FlexBox gap="small" align="center">
      <ComponentIconRepresentation
        entityType={APIEntityType.COMPONENT_TYPE}
        entityId={componentType.id}
        parentEntityId={modelId}
        enhancedScopeData={scopeData}
        graphics={graphics}
      />
      {componentType && (
        <TruncatedTextPopover>{componentType.name}</TruncatedTextPopover>
      )}
    </FlexBox>
  );
};

const WorkspaceRenderer: SharedCellRenderer<ArdoqId> = props => {
  const workspaceName = cellRendererPropsOperations.getWorkspaceName(props);

  if (
    props.data &&
    createComponentsOperations.isComponentNotPersisted(props.data._id)
  ) {
    // Show the Select for the new component row to highlight that this can be edited
    return (
      <Box width="full">
        <CellSelect
          openMenuOnClick={false} // this is set to false so that clicking on the cell triggers edit mode
          value={workspaceName}
          onValueChange={() => {}}
          options={[{ label: workspaceName, value: workspaceName }]}
          isClearable={false}
        />
      </Box>
    );
  }
  return <TruncatedTextPopover>{workspaceName}</TruncatedTextPopover>;
};

const ProvenanceRenderer: SharedCellRenderer<Provenance> = ({
  value,
  context,
}) =>
  value === undefined ? (
    <NoValue />
  ) : (
    <ProvenanceValue
      provenance={value}
      i18nLocale={context.i18nLocale}
      hasMissingRecordExplanation={false}
    />
  );

const ArdoqOIDRenderer: SharedCellRenderer<ArdoqId> = props => {
  const ardoqOid = cellRendererPropsOperations.getId(props);
  // we use temporary frontend-generated ids now new components so that AG Grid can identify rows,
  // but we don't want to show them to the user
  return ardoqOid &&
    createComponentsOperations.isComponentNotPersisted(ardoqOid) ? (
    <NoValue />
  ) : (
    <TextOverflow>{ardoqOid}</TextOverflow>
  );
};

const LucidchartCellContainer = styled(FlexBox)`
  width: 100%;
  overflow: hidden;
`;

const LucidchartTagWrapper = styled.div`
  max-width: 100%;
  overflow: hidden;
`;

const LucidchartRenderer: ComponentCellRenderer<
  PersistedExternalDocument[]
> = props => {
  const documents = cellRendererPropsOperations.getValue(props);
  if (!documents || documents.length === 0) return <NoValue />;

  return (
    <LucidchartCellContainer gap="xsmall" flexDirection="column" align="start">
      {documents.map(doc => (
        <LucidchartTagWrapper key={doc._id}>
          <TagButton
            label={doc.displayText}
            leftIcon={{ name: IconName.LUCIDCHART }}
            onClick={() => props.context.commands.openExternalDocument(doc)}
          />
        </LucidchartTagWrapper>
      ))}
    </LucidchartCellContainer>
  );
};

const ReferenceTypeRenderer = (props: ReferenceCellRendererProps<number>) => {
  const referenceType = cellRendererPropsOperations.getReferenceType(props);
  if (!referenceType) return <NoValue />;
  return <TextOverflow>{referenceType.name}</TextOverflow>;
};

const DefaultRenderer: SharedCellRenderer<ReactNode> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props))
    return <NotApplicable />;
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props))
    return <Redacted />;

  const value = cellRendererPropsOperations.getValue(props);
  return isEmpty(value) ? <NoValue /> : <TextOverflow>{value}</TextOverflow>;
};

export const FullWidthCellRenderer: ComponentCellRenderer<HelperTextRow> = (
  props: ICellRendererParams
) => {
  return (
    <FlexBox height="full" justify="center" gap="small" align="center">
      <Icon
        color={colors.grey500}
        iconSize={IconSize.SMALL}
        iconName={props.data.leftIconName}
      />
      <Text variant="caption" color="textSubtle">
        {props.data.helperText}
      </Text>
    </FlexBox>
  );
};

export const DeleteComponentButton: (
  props: ComponentCellRendererProps<ArdoqId> & { onClick: () => void }
) => ReactNode = props => {
  const { i18nLocale } = props.context;
  return (
    <GhostButton onClick={props.onClick}>
      <Icon iconName={IconName.DELETE}></Icon>
      <VisuallyHidden>
        {t(
          i18nLocale,
          'inventoryApp:ag.cellRenderer.deleteComponent',
          'Delete component'
        )}
      </VisuallyHidden>
    </GhostButton>
  );
};

const SelectMultipleListRenderer: SharedCellRenderer<string[]> = props => {
  if (!cellRendererPropsOperations.isSharedColumnApplicable(props)) {
    return <NotApplicable />;
  }
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(props)) {
    return <Redacted />;
  }

  const value = cellRendererPropsOperations.getValue(props);
  if (!value || value.length === 0) {
    return <NoValue />;
  }
  return <TruncatedTextPopover>{value.join(', ')}</TruncatedTextPopover>;
};

const TagsRenderer: ComponentCellRenderer<string[]> = props => {
  const value = cellRendererPropsOperations.getValue(props);
  if (!value || value.length === 0) return <NoValue />;

  return (
    <TagContainerWithTruncation maxHeight="calc(1rem + 12px)">
      {value.map(tag => (
        <Tag statusType={StatusType.INFO} key={tag} label={tag} />
      ))}
    </TagContainerWithTruncation>
  );
};

export const withCellErrorBoundary =
  <P extends object>(Renderer: React.FC<P>): React.FC<P> =>
  props => (
    <ErrorBoundary errorMessage={null} logError={logError}>
      <Renderer {...props} />
    </ErrorBoundary>
  );

const CellWithErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: ${s4};
`;

const CellContentWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

type ValidatedCellRendererProps = {
  colDef?: { field?: string | null } | null;
  column?: { getColId: () => string } | null;
  node?: IRowNode | null;
  eGridCell?: HTMLElement;
  context: Pick<SharedInventoryContext, 'i18nLocale'>;
};

export const withCellValidationError =
  <P extends ValidatedCellRendererProps>(Renderer: React.FC<P>): React.FC<P> =>
  props => {
    const errorMessage = cellOperations.getValidationError(props);
    const { i18nLocale } = props.context;
    const { eGridCell } = props;

    useEffect(() => {
      if (!eGridCell || !errorMessage) {
        return;
      }
      eGridCell.setAttribute('aria-invalid', 'true');
      return () => {
        eGridCell.removeAttribute('aria-invalid');
      };
    }, [eGridCell, errorMessage]);

    if (!errorMessage) {
      return <Renderer {...props} />;
    }

    return (
      <CellWithErrorWrapper>
        <CellContentWrapper>
          <Renderer {...props} />
        </CellContentWrapper>
        <WithPopover content={errorMessage}>
          <Icon
            iconName={IconName.ERROR}
            iconSize={IconSize.SMALL}
            color={colors.textError}
          />
        </WithPopover>
        <VisuallyHidden>
          {t(
            i18nLocale,
            'inventoryApp:ag.cellRenderer.errorPrefix',
            { message: errorMessage },
            'Error: {{message}}'
          )}
        </VisuallyHidden>
      </CellWithErrorWrapper>
    );
  };

export const mapColumnToAgGridCellRendererIncludingHierarchies = (
  column: ColumnWithCustomCellRenderer,
  isHierarchyEnabled: boolean
):
  | {
      cellRenderer: 'agGroupCellRenderer'; // name renderer group when hierarchy is enabled
      showRowGroup: true;
      cellRendererParams: IGroupCellRendererParams;
    }
  | {
      cellRenderer:
        | React.FC<ComponentCellRendererProps<any>>
        | CustomCellRendererName;
    } => {
  const cellRenderer =
    column.customCellRendererName ?? getComponentCellRenderer(column);

  if (columnOperations.isNameColumn(column) && isHierarchyEnabled) {
    return {
      cellRenderer: 'agGroupCellRenderer',
      showRowGroup: true,
      cellRendererParams: {
        suppressDoubleClickExpand: true,
        innerRenderer: cellRenderer,
      } satisfies IGroupCellRendererParams,
    };
  }

  return { cellRenderer };
};

type ComponentCellRendererComponent = React.FC<ComponentCellRendererProps<any>>;
type SharedCellRendererComponent = React.FC<SharedCellRendererProps<any>>;
type ReferenceCellRendererComponent = React.FC<ReferenceCellRendererProps<any>>;

const asComponentCellRenderer =
  (Renderer: SharedCellRendererComponent): ComponentCellRendererComponent =>
  props => <Renderer {...props} rowKind="component" />;

const asReferenceCellRenderer =
  (Renderer: SharedCellRendererComponent): ReferenceCellRendererComponent =>
  props => <Renderer {...props} rowKind="reference" />;

const getComponentSpecificCellRenderer = (
  column: ReportColumn
): ComponentCellRendererComponent | null => {
  if (columnOperations.isTagsColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(TagsRenderer));
  } else if (columnOperations.isNameColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(NameRenderer));
  } else if (columnOperations.isParentColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(ParentRenderer));
  } else if (columnOperations.isTypeColumn(column)) {
    return withCellErrorBoundary(
      withCellValidationError(ComponentTypeRenderer)
    );
  } else if (columnOperations.isReferenceColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(ReferenceRenderer));
  } else if (columnOperations.isLucidchartColumn(column)) {
    return withCellErrorBoundary(LucidchartRenderer);
  }
  return null;
};

const getReferenceSpecificCellRenderer = (
  column: ReportColumn
): ReferenceCellRendererComponent | null => {
  if (columnOperations.isTypeColumn(column)) {
    return withCellErrorBoundary(ReferenceTypeRenderer);
  }
  return null;
};

const getSharedCellRenderer = (
  column: ReportColumn
): SharedCellRendererComponent | null => {
  if (columnOperations.isDateOnlyColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(DateOnlyRenderer));
  } else if (columnOperations.isDateTimeColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(DateTimeRenderer));
  } else if (columnOperations.isCheckBoxColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(CheckBoxRenderer));
  } else if (columnOperations.isDateTimeRangeColumn(column)) {
    return withCellErrorBoundary(
      withCellValidationError(DateTimeRangeRenderer)
    );
  } else if (columnOperations.isDateOnlyRangeColumn(column)) {
    return withCellErrorBoundary(
      withCellValidationError(DateOnlyRangeRenderer)
    );
  } else if (columnOperations.isUserColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(UserRenderer));
  } else if (columnOperations.isTextAreaColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(TextAreaRenderer));
  } else if (columnOperations.isNumberColumn(column)) {
    return withCellErrorBoundary(
      withCellValidationError(getNumberRenderer(column))
    );
  } else if (columnOperations.isURLColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(URLRenderer));
  } else if (columnOperations.isEmailColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(EmailRenderer));
  } else if (columnOperations.isWorkspaceColumn(column)) {
    return withCellErrorBoundary(withCellValidationError(WorkspaceRenderer));
  } else if (columnOperations.isArdoqOID(column)) {
    return withCellErrorBoundary(withCellValidationError(ArdoqOIDRenderer));
  } else if (columnOperations.isSelectMultipleListColumn(column)) {
    return withCellErrorBoundary(
      withCellValidationError(SelectMultipleListRenderer)
    );
  } else if (columnOperations.isProvenanceColumn(column)) {
    return withCellErrorBoundary(ProvenanceRenderer);
  }
  return null;
};

const getDefaultCellRenderer = (): SharedCellRendererComponent =>
  withCellErrorBoundary(withCellValidationError(DefaultRenderer));

export const getReferenceCellRenderer = (
  column: ReportColumn
): ReferenceCellRendererComponent =>
  getReferenceSpecificCellRenderer(column) ??
  asReferenceCellRenderer(
    getSharedCellRenderer(column) ?? getDefaultCellRenderer()
  );

export const getComponentCellRenderer = (
  column: ReportColumn
): ComponentCellRendererComponent =>
  getComponentSpecificCellRenderer(column) ??
  asComponentCellRenderer(
    getSharedCellRenderer(column) ?? getDefaultCellRenderer()
  );
